import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { flatMap, map, toArray } from 'rxjs/operators';

import { buList } from '../../shared/i18n/i18n.config';
import { implementsCrmUser } from '../../utils';
import { CreateUserAccess, UpdateUserAccess, UserAccess } from '../models/access';
import { BackOfficeUser, SalesForceSearchParameters, SearchType } from '../models/backOfficeUser';
import { CMSAssociationResult } from '../models/installer';

import { AdminAuthService } from './auth.service';
import { AdminHttpService } from './http.service';

@Injectable()
export class BackOfficeService {
    constructor(
        private adminHttpService: AdminHttpService,
        private authService: AdminAuthService,
    ) {}

    private computeBUList(partnerBuList: string[]): string[] {
        let buIds: string[];
        if (partnerBuList.length === 1) {
            if (partnerBuList[0] === 'all') {
                buIds = [...buList.map((bu) => bu.value)];
            } else {
                buIds = [...partnerBuList];
            }
        } else {
            buIds = [...partnerBuList];
        }
        return buIds;
    }

    private mapFilterUserWithoutBusinessOrCRMId(v: CMSAssociationResult[]): CMSAssociationResult[] {
        return v.map((assocRes: CMSAssociationResult): CMSAssociationResult => {
            const filteredInstaller = assocRes.usersBu.filter((u) => implementsCrmUser(u));
            return {
                totalUsers: filteredInstaller.length,
                usersBu: filteredInstaller,
            };
        });
    }

    getAdminUsers(): Observable<UserAccess[]> {
        return this.adminHttpService.get(`/v1/bo/admin/users`);
    }

    updateUserAccess(userAccess: CreateUserAccess | UpdateUserAccess): Observable<UserAccess> {
        return this.adminHttpService.post(`/v1/bo/admin/access`, userAccess);
    }

    setUserPassword(password: string, verificationHash: string): Observable<void> {
        return this.adminHttpService.post(`/v1/bo/admin/access/password?token=${verificationHash}`, { password });
    }

    resetUserAccessPassword(userEmail: string): Observable<void> {
        return this.adminHttpService.post(`/v1/bo/admin/access/reset/`, {
            userEmail,
        });
    }

    removeUserAccess(userId: string): Observable<string> {
        return this.adminHttpService.delete(`/v1/bo/admin/access/delete/${userId}`);
    }

    getStats(): Observable<string> {
        return this.adminHttpService.get(`/v1/bo/stats`);
    }

    getCurrentBackOfficeUserInfo(): Observable<BackOfficeUser> {
        return this.authService.getCurrentLoggedInAdminUser();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    advancedInstallerSearch(partnerBuList: string[], fieldValues: any, strictMode = false) {
        const offset = 0;
        const limit = 100;

        const operator = strictMode ? ' AND ' : ' OR ';
        const logic = Object.keys(fieldValues).join(operator);

        const searchParams: SalesForceSearchParameters = {
            mode: SearchType.QUERY,
            fieldsValue: {
                ...fieldValues,
                logic,
            },
        };

        const buIds: string[] = this.computeBUList(partnerBuList);

        return from(buIds).pipe(
            flatMap((buId: string) =>
                this.adminHttpService.post(
                    `/v1/bo/users/${buId}/associations?offset=${offset}&limit=${limit}`,
                    searchParams,
                ),
            ),
            toArray(),
            map(this.mapFilterUserWithoutBusinessOrCRMId),
        );
    }

    closeAllPendingInterventions(ssoId: string) {
        return this.adminHttpService.post(`/v1/bo/intervention/${ssoId}`, {});
    }
}
